import {
  EnvironmentProviders,
  inject,
  Injectable,
  InjectionToken,
  makeEnvironmentProviders,
  provideAppInitializer,
  signal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, tap } from 'rxjs';
import { OrgCountryEnum } from '@rezevent/api-types';

/** True only in apps that call provideTranslations() and ship assets/i18n/*.json */
export const TRANSLATIONS_ENABLED = new InjectionToken<boolean>('TRANSLATIONS_ENABLED', {
  providedIn: 'root',
  factory: () => false,
});

function flatten(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
  return Object.entries(obj).reduce(
    (acc, [key, value]) => {
      const full = prefix ? `${prefix}.${key}` : key;
      if (value !== null && typeof value === 'object') {
        Object.assign(acc, flatten(value as Record<string, unknown>, full));
      } else {
        acc[full] = String(value);
      }
      return acc;
    },
    {} as Record<string, string>
  );
}

@Injectable({ providedIn: 'root' })
export class TranslationService {
  private http = inject(HttpClient);
  private _map = signal<Record<string, string>>({});

  translate(key: string): string {
    return this._map()[key] ?? key;
  }

  load(country: OrgCountryEnum | null) {
    const lang = country === OrgCountryEnum.De ? 'de' : 'sq';
    return this.http.get<Record<string, unknown>>(`/assets/i18n/${lang}.json`).pipe(
      tap(raw => this._map.set(flatten(raw))),
      map(() => void 0)
    );
  }
}

/** Enables translations: loads the stored country's file on startup and after login. */
export function provideTranslations(countryStorageKey: string): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: TRANSLATIONS_ENABLED, useValue: true },
    provideAppInitializer(() => {
      const country = localStorage.getItem(countryStorageKey) as OrgCountryEnum | null;
      return inject(TranslationService).load(country);
    }),
  ]);
}
